import { apiWithPrefix as api } from '../utils/api';

/**
 * Attendance Service
 * Handles all attendance-related API calls
 */
export class AttendanceService {
  /**
   * Get attendance records
   */
  static async getAttendance(startDate, endDate, email = null) {
    try {
      const params = { startDate, endDate };
      if (email) params.email = email;
      
      const response = await api.get('/attendance', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch attendance data');
    }
  }

  /**
   * Manual attendance mark API intentionally disabled.
   * Keeping commented for future re-enable if required.
   */
  // static async markAttendance(attendanceData) {
  //   try {
  //     const response = await api.post('/attendance/mark', attendanceData);
  //     return response.data;
  //   } catch (error) {
  //     throw new Error(error.response?.data?.message || 'Failed to mark attendance');
  //   }
  // }

  static async markAttendance(attendanceData) {
    try {
      const response = await api.post('/attendance/mark', attendanceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark attendance');
    }
  }

  /**
   * Create attendance rectification request
   */
  static async createRectificationRequest(data) {
    try {
      const response = await api.post('/attendance-reactification-request', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create rectification request');
    }
  }

  /**
   * Get user's rectification requests
   */
  static async getRectificationRequests(page = 1) {
    try {
      const response = await api.get(`/attendance-reactification-request/latest?page=${page}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch rectification requests');
    }
  }

  /**
   * Get rectification requests for manager approval inbox
   * @param {number} page
   * @param {'pending'|'accepted'} status
   */
  static async getRectificationRequestsByManager(page = 1, status = 'pending') {
    try {
      const response = await api.get(
        `/attendance-reactification-request-by-manager/latest?page=${page}&status=${status}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch manager rectification requests');
    }
  }

  /**
   * Update rectification request (approve/reject)
   */
  static async updateRectificationRequest(id, data) {
    try {
      const response = await api.put(`/attendance-reactification-request-by-manager/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update rectification request');
    }
  }

  /**
   * Manual mark absent
   */
  static async markAbsent(data) {
    try {
      const response = await api.post('/manualMarkAbsent', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark absent');
    }
  }

  /**
   * Get attendance summary
   */
  static async getAttendanceSummary(period = 'month') {
    try {
      const response = await api.get('/attendance/summary', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch attendance summary');
    }
  }

  /**
   * Get today's attendance
   */
  static async getTodayAttendance() {
    try {
      const today = new Date();
      const todayString = this.formatDate(today);
      
      const response = await api.get('/attendance', {
        params: {
          startDate: todayString,
          endDate: todayString
        }
      });
      
      const attendanceData = response.data?.attendanceData || [];
      return attendanceData.find(entry => {
        const entryDate = new Date(entry.datestring);
        return this.formatDate(entryDate) === todayString;
      });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch today\'s attendance');
    }
  }

  /**
   * Get attendance statistics
   */
  static async getAttendanceStats(startDate, endDate) {
    try {
      const response = await api.get('/attendance/stats', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch attendance statistics');
    }
  }

  /**
   * Export attendance report
   */
  static async exportAttendanceReport(startDate, endDate, format = 'excel') {
    try {
      const response = await api.get('/attendance/export', {
        params: { startDate, endDate, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export attendance report');
    }
  }

  /**
   * Get employees for admin selection
   */
  static async getEmployeesForSelection() {
    try {
      const response = await api.get('/attendance/admin-employees');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employees');
    }
  }

  /**
   * Admin mark attendance for multiple employees
   */
  static async adminMarkAttendance(data) {
    try {
      const response = await api.post('/attendance/admin-mark', data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark attendance');
    }
  }

  /**
   * Helper method to format date
   */
  static formatDate(date) {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  /**
   * Calculate work hours
   */
  static calculateWorkHours(inTime, outTime, breakTime = '00:00:00') {
    if (!inTime || !outTime) return '00:00:00';
    
    try {
      const inDate = new Date(`1970-01-01T${inTime}`);
      const outDate = new Date(`1970-01-01T${outTime}`);
      const breakMinutes = this.timeToMinutes(breakTime);
      
      const diffMs = outDate - inDate;
      const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
      
      return this.minutesToTime(diffMinutes);
    } catch (error) {
      return '00:00:00';
    }
  }

  /**
   * Convert time string to minutes
   */
  static timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }

  /**
   * Convert minutes to time string
   */
  static minutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  }
}

export default AttendanceService;
